import { PACKAGES, ARRANGEMENTS, arrangementPackageCount } from 'components/paperwork/content';
import { MAX_CART_LINES, equipmentById, stateEditionByCode } from 'data/paperworkVariants';
import { PACKAGE_IDS, chargePrice, customizationPrice, isDeposit, packagePrice } from 'lib/paperworkPrices';

export const CART_STORAGE_KEY = 'magellan-paperwork-cart-v1';
export const CART_LIMIT = 10;
export const formatCartMoney = minor => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: minor % 100 ? 2 : 0 }).format(minor / 100);
export const cartItemKey = (packageId, arrangement, equipment, state) => [packageId, arrangement, equipment || '', state || ''].join(':');
const itemKey = item => cartItemKey(item.packageId, item.arrangement, item.equipment, item.state);
export function unfinishedCartItem(items, packageId, arrangement, equipment) {
  const matches = items.filter(item => item.packageId === packageId && item.arrangement === arrangement && !stateEditionByCode(item.state));
  return matches.find(item => item.equipment === equipment) || matches.find(item => !equipmentById(item.equipment));
}

export function normalizeCartItems(items) {
  if (!Array.isArray(items)) return [];
  const found = new Map();
  for (const entry of items.slice(0, 270)) {
    if (!entry || typeof entry.packageId !== 'string' || typeof entry.arrangement !== 'string'
      || !PACKAGE_IDS.includes(entry.packageId) || !ARRANGEMENTS.some(a => a.id === entry.arrangement)
      || !Number.isInteger(entry.quantity) || entry.quantity < 1 || entry.quantity > CART_LIMIT) continue;
    // Preserve earlier cart selections for correction; never invent equipment or
    // a state edition when restoring an older or malformed saved cart.
    const equipment = equipmentById(entry.equipment)?.id || null;
    const state = equipment ? stateEditionByCode(entry.state)?.code || null : null;
    const key = cartItemKey(entry.packageId, entry.arrangement, equipment, state);
    if (!found.has(key) && found.size >= MAX_CART_LINES) continue;
    const customized = entry.packageId === 'basic' && (entry.customized === true || found.get(key)?.customized === true);
    found.set(key, { packageId: entry.packageId, arrangement: entry.arrangement, equipment, state, quantity: Math.min(CART_LIMIT, (found.get(key)?.quantity || 0) + entry.quantity), ...(customized ? { customized: true } : {}) });
  }
  return [...found.values()];
}
export function cartItemView(entry) {
  const pkg = PACKAGES.find(p => p.id === entry.packageId);
  const equipment = equipmentById(entry.equipment), state = stateEditionByCode(entry.state);
  const deposit = isDeposit(entry.packageId, entry.arrangement);
  return { ...entry, key: itemKey(entry), name: pkg.name, description: pkg.description,
    arrangementLabel: ARRANGEMENTS.find(a => a.id === entry.arrangement).label, deposit,
    paperworkPackageCount: arrangementPackageCount(entry.arrangement), packagePriceMinor: packagePrice(entry.packageId, entry.arrangement),
    ...(deposit && entry.customized ? { deferredCustomizationMinor: customizationPrice(entry.arrangement) } : {}),
    priceMinor: entry.customized && !deposit ? (chargePrice(entry.packageId, entry.arrangement) === undefined || customizationPrice(entry.arrangement) === undefined ? undefined : chargePrice(entry.packageId, entry.arrangement) + customizationPrice(entry.arrangement)) : chargePrice(entry.packageId, entry.arrangement),
    equipmentLabel: equipment?.label || '', stateLabel: state?.name || '', configurationComplete: !!(equipment && state) };
}
export function readCart() {
  try {
    const stored = JSON.parse(localStorage.getItem(CART_STORAGE_KEY) || '{}');
    return { items: normalizeCartItems(stored.items), completedOrders: validOrderIds(stored.completedOrders) };
  } catch { return { items: [], completedOrders: [] }; }
}
function validOrderIds(ids) {
  return Array.isArray(ids) ? [...new Set(ids.filter(id => typeof id === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(id)))].slice(-30) : [];
}
export function cartReducer(state, action) {
  if (action.type === 'hydrate') return { items: normalizeCartItems(action.value?.items), completedOrders: validOrderIds(action.value?.completedOrders) };
  if (action.type === 'add') {
    if (!equipmentById(action.equipment) || !stateEditionByCode(action.state)) return state;
    // Completing a saved unfinished selection replaces that unfinished row.
    const prior = unfinishedCartItem(state.items, action.packageId, action.arrangement, action.equipment);
    const remaining = prior ? state.items.filter(item => item !== prior) : state.items;
    return { ...state, items: normalizeCartItems([...remaining, { packageId: action.packageId, arrangement: action.arrangement, equipment: action.equipment, state: action.state, quantity: prior?.quantity || 1 }]) };
  }
  if (action.type === 'remove') return { ...state, items: state.items.filter(item => itemKey(item) !== action.key) };
  // "Undo" after removing a line (UX upgrade 6): the line comes back unless the same selection was added again meanwhile.
  if (action.type === 'restore') {
    if (!action.item || state.items.some(item => itemKey(item) === itemKey(action.item))) return state;
    const at = Number.isInteger(action.index) ? Math.max(0, Math.min(action.index, state.items.length)) : state.items.length;
    const items = normalizeCartItems([...state.items.slice(0, at), action.item, ...state.items.slice(at)]);
    return items.length > state.items.length ? { ...state, items } : state;
  }
  if (action.type === 'customize') return { ...state, items: state.items.map(item => itemKey(item) === action.key && item.packageId === 'basic' ? (action.customized ? { ...item, customized: true } : (({ customized, ...rest }) => rest)(item)) : item) };
  if (action.type === 'quantity') {
    if (!Number.isInteger(action.quantity) || action.quantity < 1 || action.quantity > CART_LIMIT) return state;
    return { ...state, items: state.items.map(item => itemKey(item) === action.key ? { ...item, quantity: action.quantity } : item) };
  }
  if (action.type === 'clear') {
    if (!action.orderId) return { ...state, items: [] };
    if (!validOrderIds([action.orderId]).length) return state;
    if (state.completedOrders.includes(action.orderId)) return state;
    const purchased = normalizeCartItems(action.items);
    const items = state.items.map(item => ({ ...item, quantity: item.quantity - (purchased.find(p => itemKey(p) === itemKey(item))?.quantity || 0) })).filter(item => item.quantity > 0);
    return { items, completedOrders: validOrderIds([...state.completedOrders, action.orderId]) };
  }
  return state;
}
